import { perfMonitor } from '../utils/performanceMarkers';

/** Named marks used across Gantt load / edit / save paths. */
export const GANTT_PERF_MARKS = {
  hydrate: 'gantt-hydrate',
  apiFetch: 'gantt-api-fetch',
  transform: 'gantt-transform',
  setTasks: 'gantt-setTasks',
  reactClone: 'gantt-react-clone',
  firstPaint: 'gantt-first-paint',
  saveApis: 'gantt-save-apis',
  saveRefetch: 'gantt-save-refetch',
  autoscheduleTouched: 'gantt-autoschedule-touched',
  autoscheduleRealDirty: 'gantt-autoschedule-real-dirty',
} as const;

/** Soft budgets for ~15k-task projects (dev guidance). */
export const GANTT_PERF_BUDGETS_MS = {
  hydrateReentry: 1000,
  dragDump: 100,
  saveWithoutFullRefetchVsPreviousSavePlusRefetch: 'lower',
} as const;

/**
 * Regression checklist — must stay green when changing Gantt perf / dirty / save paths.
 * - Drag task / project; auto-schedule cascade
 * - True cascade still dirties: change predecessor finish with Live ON or Recalculate —
 *   dependents with moved dates appear in Save count
 * - False dirty suppressed: after clean Save (+ no full refetch or after refetch),
 *   Recalculate with no edits → Save count stays 0 (or near-0 if calendar forces a real snap)
 * - Add / delete / link create-delete; Save then refresh page — data matches
 * - Discard restores server state
 * - Schedule ↔ Targets toggle and zoom do not trigger clearAll+parse
 * - Baseline compare columns still correct
 * - Leave with dirty changes prompts and does not leak stale cache incorrectly
 * - Predecessor/Successor columns show the same values, faster
 */
export const GANTT_PERF_REGRESSION_CHECKLIST = [
  'drag-and-autoschedule-cascade',
  'cascade-keeps-dhtmlxTasks-array-identity-no-reactClone',
  'true-cascade-dirties-save-count',
  'false-dirty-suppressed-after-recalculate',
  'crud-save-refresh-matches',
  'discard-restores-server',
  'view-zoom-no-clearAll-parse',
  'baseline-compare-columns',
  'dirty-leave-cache-policy',
  'pred-succ-column-parity',
] as const;

export function markGanttPerf(label: keyof typeof GANTT_PERF_MARKS): void {
  perfMonitor.mark(GANTT_PERF_MARKS[label]);
}

export function measureGanttPerf(label: keyof typeof GANTT_PERF_MARKS): number | undefined {
  return perfMonitor.measure(GANTT_PERF_MARKS[label]);
}

/** Dev-only ratio log after Recalculate / auto-schedule dirty gate. */
export function logAutoscheduleDirtyRatio(touched: number, realDirty: number): void {
  if (!import.meta.env.DEV) return;
  const ratio = touched === 0 ? 0 : realDirty / touched;
  console.log(
    `[PERF] ${GANTT_PERF_MARKS.autoscheduleRealDirty}/${GANTT_PERF_MARKS.autoscheduleTouched}: ${realDirty}/${touched} (ratio ${ratio.toFixed(3)})`,
  );
}
